import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { backupApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Download,
  Upload,
  Trash2,
  Database,
  ShieldAlert,
  PackageCheck,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Info,
} from 'lucide-react';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 1)} ${units[i]}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminBackup() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [deletingTarget, setDeletingTarget] = useState(null);

  // Restore flow state
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [restorePhrase, setRestorePhrase] = useState('');
  const [restoreUploading, setRestoreUploading] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const fileInputRef = useRef(null);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const res = await backupApi.list();
      setBackups(res.data.backups || []);
    } catch (err) {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBackups(); }, []);

  const triggerAutoDownload = async (filename) => {
    setDownloading(filename);
    try {
      const res = await backupApi.download(filename);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    const toastId = toast.loading('Creating backup… this may take a few minutes for large datasets.');
    try {
      const res = await backupApi.create();
      const meta = res.data;
      toast.success(`Backup created: ${meta.filename} (${formatBytes(meta.size_bytes)})`, { id: toastId });
      await loadBackups();
      // Auto-download to user's laptop — the whole point of this feature.
      await triggerAutoDownload(meta.filename);
      toast.success('Backup downloaded to your computer.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Backup failed';
      toast.error(msg, { id: toastId });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (filename) => {
    try {
      await backupApi.delete(filename);
      toast.success(`Deleted ${filename}`);
      setDeletingTarget(null);
      await loadBackups();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleRestoreSubmit = async (e) => {
    e?.preventDefault?.();
    if (!restoreFile) {
      toast.error('Please select a backup .zip file');
      return;
    }
    if (restorePhrase !== 'RESTORE') {
      toast.error('Type RESTORE exactly to confirm');
      return;
    }
    if (!restorePassword) {
      toast.error('Enter your admin password');
      return;
    }
    setRestoreUploading(true);
    setRestoreProgress(0);
    const toastId = toast.loading('Uploading & restoring… do not close this tab.');
    try {
      const res = await backupApi.restore(restoreFile, restorePassword, (evt) => {
        if (evt.total) setRestoreProgress(Math.round((evt.loaded * 100) / evt.total));
      });
      const stats = res.data;
      const colCount = Object.keys(stats.restored_collections || {}).length;
      toast.success(
        `Restore complete: ${colCount} collections, ${stats.files_restored} files. Safety snapshot: ${stats.safety_snapshot || 'skipped'}`,
        { id: toastId, duration: 8000 },
      );
      setRestoreOpen(false);
      setRestoreFile(null);
      setRestorePassword('');
      setRestorePhrase('');
      // Force re-auth because users collection just changed
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Restore failed';
      toast.error(msg, { id: toastId });
    } finally {
      setRestoreUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-backup-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Database className="w-7 h-7 text-blue-700" />
              Backup &amp; Restore
            </h1>
            <p className="text-slate-600 mt-1">
              Download a complete snapshot before every re-deploy. Upload to restore if anything is missing.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadBackups}
            disabled={loading}
            data-testid="refresh-backups-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-700" />
          <AlertTitle className="text-blue-900">How this protects you</AlertTitle>
          <AlertDescription className="text-blue-900/90">
            Every backup is a single <code>.zip</code> containing all MongoDB collections,
            every uploaded document, all audit logs, and system settings. The file
            automatically downloads to your computer. <strong>Save it before every re-deploy.</strong>
            If anything goes wrong, upload it back with the Restore button and the system
            returns to that exact moment.
          </AlertDescription>
        </Alert>

        {/* Create Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-emerald-600" />
              Create New Backup
            </CardTitle>
            <CardDescription>
              Builds a fresh snapshot, saves it on the server, and downloads it to your computer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              size="lg"
              onClick={handleCreateBackup}
              disabled={creating}
              data-testid="create-backup-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Backing up… (don&apos;t close this tab)
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Backup Now &amp; Download
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Existing Backups */}
        <Card>
          <CardHeader>
            <CardTitle>Available Backups</CardTitle>
            <CardDescription>
              Last {backups.length} backup{backups.length === 1 ? '' : 's'} stored on the server.
              Older backups are auto-deleted to save disk space.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-slate-500">
                <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
                Loading…
              </div>
            ) : backups.length === 0 ? (
              <div className="py-8 text-center text-slate-500 border border-dashed rounded-lg">
                No backups yet. Click <strong>Backup Now</strong> above to create your first one.
              </div>
            ) : (
              <div className="space-y-3" data-testid="backups-list">
                {backups.map((b) => (
                  <div
                    key={b.filename}
                    data-testid={`backup-item-${b.filename}`}
                    className="flex items-center justify-between border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-semibold text-slate-900 truncate">
                        {b.filename}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                        <span>{formatTime(b.created_at)}</span>
                        <Badge variant="secondary">{formatBytes(b.size_bytes)}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => triggerAutoDownload(b.filename)}
                        disabled={downloading === b.filename}
                        data-testid={`download-backup-${b.filename}`}
                      >
                        {downloading === b.filename ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeletingTarget(b)}
                        data-testid={`delete-backup-${b.filename}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-900">
              <ShieldAlert className="w-5 h-5" />
              Restore from Backup
            </CardTitle>
            <CardDescription className="text-red-700/80">
              Upload a backup <code>.zip</code> to overwrite ALL current data. A safety snapshot
              of the current state is taken automatically before the restore begins.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setRestoreOpen(true)}
              data-testid="open-restore-dialog-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Restore from Backup…
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation */}
      <Dialog open={!!deletingTarget} onOpenChange={(o) => !o && setDeletingTarget(null)}>
        <DialogContent data-testid="delete-backup-dialog">
          <DialogHeader>
            <DialogTitle>Delete backup?</DialogTitle>
            <DialogDescription>
              This removes the backup file from the server. If you&apos;ve already downloaded a
              copy, you can still restore from your local copy.
            </DialogDescription>
          </DialogHeader>
          <div className="font-mono text-sm bg-slate-100 rounded p-2 break-all">
            {deletingTarget?.filename}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deletingTarget.filename)}
              data-testid="confirm-delete-backup-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={restoreOpen} onOpenChange={(o) => !restoreUploading && setRestoreOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-900">
              <AlertTriangle className="w-5 h-5" />
              Restore from Backup
            </DialogTitle>
            <DialogDescription>
              This will <strong>overwrite all current data</strong> with the contents of the
              uploaded backup. A safety snapshot is created first so you can roll back.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRestoreSubmit} className="space-y-4">
            <div>
              <Label htmlFor="backup-file">Backup file (.zip)</Label>
              <Input
                ref={fileInputRef}
                id="backup-file"
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                disabled={restoreUploading}
                data-testid="restore-file-input"
              />
              {restoreFile && (
                <div className="text-xs text-slate-500 mt-1">
                  Selected: {restoreFile.name} ({formatBytes(restoreFile.size)})
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="restore-password">Your admin password</Label>
              <Input
                id="restore-password"
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                disabled={restoreUploading}
                autoComplete="current-password"
                data-testid="restore-password-input"
              />
            </div>

            <div>
              <Label htmlFor="restore-phrase">
                Type <code className="text-red-700 font-bold">RESTORE</code> to confirm
              </Label>
              <Input
                id="restore-phrase"
                type="text"
                value={restorePhrase}
                onChange={(e) => setRestorePhrase(e.target.value)}
                disabled={restoreUploading}
                placeholder="RESTORE"
                data-testid="restore-phrase-input"
              />
            </div>

            {restoreUploading && (
              <div>
                <div className="text-sm text-slate-700 mb-1">Uploading… {restoreProgress}%</div>
                <div className="w-full bg-slate-200 rounded h-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-2 transition-all"
                    style={{ width: `${restoreProgress}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  After upload completes, the server will validate, snapshot, wipe, and restore.
                  This may take a few minutes for large backups.
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRestoreOpen(false)}
                disabled={restoreUploading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={restoreUploading || restorePhrase !== 'RESTORE' || !restoreFile || !restorePassword}
                data-testid="confirm-restore-btn"
              >
                {restoreUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Restoring…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Restore Now
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
